import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Card } from './common'
import { t } from '../i18n/lingua'

/**
 * Il benvenuto al primo accesso: dove stanno le cose e a cosa servono.
 *
 * Compare in cima alla dashboard finché non lo si chiude — la scelta resta nel
 * browser, come la composizione dei widget: è una preferenza di lettura, non un
 * dato del tenant. Chi entra la prima volta si trova davanti una mappa della
 * console invece di un menu di trenta voci da decifrare da solo; chi c'è già
 * stato lo salta con un clic e non lo rivede.
 *
 * Non è un tour a passi che insegue il cursore: quelli si saltano e non si
 * rileggono. È una pagina sola, con i link alle cose, e il rimando alla Guida
 * per il resto.
 */
const CHIAVE = 'censo.onboarding.dismissed'

function giaChiuso(): boolean {
  try {
    return localStorage.getItem(CHIAVE) === '1'
  } catch {
    return false
  }
}

export default function Benvenuto() {
  const [chiuso, setChiuso] = useState(giaChiuso)
  if (chiuso) return null

  function chiudi() {
    try {
      localStorage.setItem(CHIAVE, '1')
    } catch {
      // Modalità privata: resta chiuso per questa sessione.
    }
    setChiuso(true)
  }

  // Le aree della console, nell'ordine in cui si lavora. Le frasi passano da
  // `t()` qui (letterali che la prova delle traduzioni riconosce), non da una
  // variabile — è il motivo per cui l'elenco vive dentro il componente.
  const aree: { to: string; titolo: string; cosa: string }[] = [
    { to: '/cis', titolo: t('Inventario'),
      cosa: t('Centralizza Configuration Item, Asset e Contratti, consolida i dati tecnici acquisiti e li aggiorna mediante discovery.') },
    { to: '/topology', titolo: t('Rete e topologia'),
      cosa: t('Visualizza asset, interfacce di rete e topologia delle connessioni.') },
    { to: '/services', titolo: t('Business service'),
      cosa: t('Mappa i servizi, le dipendenze degli asset e ne evidenzia gli impatti in caso di indisponibilità.') },
    { to: '/flows', titolo: t('Traffico'),
      cosa: t('Comunicazioni tra asset, servizi esposti e dati scambiati (con NetFlow attivo).') },
    { to: '/security-health', titolo: t('Sicurezza'),
      cosa: t('Postura di sicurezza degli host, conformità NIS2 e DORA, monitoraggio tramite Dobermann e Honeypot.') },
    { to: '/fleet', titolo: t('Raccolta'),
      cosa: t('Configurazione di sonde, credenziali, piani di discovery, job e relative pianificazioni.') },
    { to: '/connectors', titolo: t('Integrazioni'),
      cosa: t('Integrazione e scambio dati con piattaforme ITSM come ServiceNow, Matrix42, Remedy e ManageEngine.') },
    { to: '/audit', titolo: t('Amministrazione'),
      cosa: t('Gestione utenti, impostazioni, riconoscimento degli asset e audit di accessi e modifiche.') },
  ]

  const passi: { to: string; testo: string }[] = [
    { to: '/fleet', testo: t('Configura una sonda nella rete da monitorare.') },
    { to: '/credentials', testo: t('Configura le credenziali di accesso: SNMP, SSH e WinRM.') },
    { to: '/discovery-plans', testo: t('Configura un piano di discovery definendo target e modalità di scansione.') },
    { to: '/jobs', testo: t('Avvia il primo job di discovery: i Configuration Item rilevati vengono registrati nell’Inventario.') },
  ]

  return (
    <Card
      title={t('Benvenuto in CENSO')}
      actions={
        <button type="button" className="secondary" onClick={chiudi}>
          {t('Salta')}
        </button>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {t('CENSO rileva automaticamente gli asset presenti in rete, individua le funzionalità e mantiene aggiornate le informazioni.')}
      </p>

      <div className="onboarding-grid">
        {aree.map((a) => (
          <Link key={a.to} to={a.to} className="onboarding-card">
            <span className="onboarding-card__title">{a.titolo}</span>
            <span className="onboarding-card__what">{a.cosa}</span>
          </Link>
        ))}
      </div>

      <div className="onboarding-start">
        <h3 className="onboarding-start__title">{t('Da dove si comincia')}</h3>
        <ol className="onboarding-steps">
          {passi.map((p, i) => (
            <li key={p.to}>
              <span className="onboarding-steps__n">{i + 1}</span>
              <Link to={p.to}>{p.testo}</Link>
            </li>
          ))}
        </ol>
        <p className="field__hint">
          {(() => {
            // Una frase sola, con il link al posto del segnaposto.
            const [prima, dopo = ''] = t('La {guida} descrive funzioni e sequenza operativa di ogni area. Il tab «Come funziona questa pagina» fornisce indicazioni contestuali.').split('{guida}')
            return (
              <>
                {prima}
                <Link to="/guide">{t('Guida')}</Link>
                {dopo}
              </>
            )
          })()}
        </p>
      </div>
    </Card>
  )
}
